/**
 * Season badge picks (Bobbie, 2026-08-17 evening: "Can the user pick the
 * avatar the first time they sign in on that day... especially the dancers,
 * but really everybody can have the option").
 *
 * The first home visit inside a live holiday window offers the season's
 * badge set (SeasonBadgePicker.tsx); the answer lands here. "Surprise me"
 * stores badge: null, which means "keep the dealt one" AND "stop asking".
 *
 * KEYED BY SEASON *AND* PERSON, not season alone. A parent and a dancer
 * routinely share one device in this app, and a single per-season slot
 * meant whoever picked second silently overwrote the first and the second
 * person was never offered the card at all (adversarial review finding,
 * 2026-08-17). Each person's picks live under their own display labels; a
 * pick is stored under EVERY label the app might render them by (auth
 * displayName and the teacher-title variant differ for staff), so the
 * badge follows them across surfaces without fuzzy matching.
 *
 * DEVICE-LOCAL BY DESIGN (v1). The pick lives in localStorage, so it
 * dresses this person's avatar and greeting on THIS device; everyone else
 * still sees the name-hash badge from seasonalAvatarBadge. Making a pick
 * follow the person across devices and into other people's views means
 * shared data plus rules work - that is queued as its own piece, not
 * smuggled into a cosmetic pass.
 *
 * useSeasonBadgePickVersion() is the reactivity: pickers dispatch an event
 * on save, and any component that renders a badge subscribes so the new
 * pick paints immediately rather than on the next navigation.
 */
import { useSyncExternalStore } from 'react';
import type { SeasonDef } from './season';

const STORAGE_KEY = 'ldg-season-badge-pick';
const CHANGE_EVENT = 'ldg-season-badge-pick-changed';

export interface SeasonBadgePick {
  /** The chosen badge, or null for "surprise me, keep the dealt one". */
  badge: string | null;
}

/** seasonKey -> display label -> that person's answer. */
type PickMap = Record<string, Record<string, SeasonBadgePick>>;

function readMap(): PickMap {
  try {
    // The typeof check has to be INSIDE the try: the localStorage getter
    // itself can throw (Safari "Block All Cookies"), so typeof evaluates it
    // and throws right along with a direct access. This runs at render in
    // the signed-in home greeting (seasonGreetingEmoji) and the avatar badges
    // whenever a season is live, so a throw here is a whole-page crash.
    if (typeof localStorage === 'undefined') return {};
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as PickMap) : {};
  } catch {
    return {};
  }
}

function cleanLabels(labels: readonly string[]): string[] {
  return [...new Set(labels.map((l) => l.trim()).filter(Boolean))];
}

/** This person's stored answer for the season, or null if never asked. */
export function getSeasonBadgePick(
  seasonKey: string,
  labels: readonly string[]
): SeasonBadgePick | null {
  const forSeason = readMap()[seasonKey];
  if (!forSeason || typeof forSeason !== 'object') return null;
  for (const label of cleanLabels(labels)) {
    const entry = forSeason[label];
    if (entry && typeof entry === 'object' && 'badge' in entry) return entry;
  }
  return null;
}

/** Store one person's answer under every label the app renders them by. */
export function setSeasonBadgePick(
  seasonKey: string,
  labels: readonly string[],
  badge: string | null
): void {
  const clean = cleanLabels(labels);
  if (clean.length === 0) return;
  try {
    const map = readMap();
    const forSeason =
      map[seasonKey] && typeof map[seasonKey] === 'object' ? map[seasonKey] : {};
    for (const label of clean) forSeason[label] = { badge };
    map[seasonKey] = forSeason;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Private mode: the event below still repaints this page's session.
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * The greeting's seasonal emoji: this person's picked badge when they have
 * one, the season's default otherwise.
 */
export function seasonGreetingEmoji(season: SeasonDef, labels: readonly string[]): string {
  const pick = getSeasonBadgePick(season.key, labels);
  if (pick?.badge && season.badges.includes(pick.badge)) return pick.badge;
  return season.greetingEmoji;
}

let version = 0;

function subscribe(onStoreChange: () => void): () => void {
  const bump = () => {
    version += 1;
    onStoreChange();
  };
  // Cross-tab: only OUR storage key matters (e.key null means a clear()).
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) bump();
  };
  window.addEventListener(CHANGE_EVENT, bump);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, bump);
    window.removeEventListener('storage', onStorage);
  };
}

/** Subscribe a badge-rendering component to pick changes. */
export function useSeasonBadgePickVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => 0);
}
